// #336 slice 6 part 2b, brief §7.3 and §8.3: a range of cells of the wide
// day grid, read and written through one lens. A cell is one exercise (the
// row) in one round (the column). Typing in a cell writes that round only,
// so a value that differs from the rule is an exception, as by hand in the
// round ladder: no new write path. Only «Sets» is written, through
// setSetCountInRound. Effort, reps and load are read only: a round has several
// sets, and one cell value would flatten them (#336, decision of 14/10).
// Keep free of React and any UI import.
import type { DraftLineOptions } from "./previewFormat.js";
import type { Draft, SlotDraft } from "./programEdit.js";
import { setCountAt } from "./programEditRoundSets.js";
import { formatDraftGridCell, type DraftGridLens } from "./programGridCell.js";
import { setSetCountInRound } from "./programRoundEdits.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";

/** One cell: `row` is the exercise's index in the day, `col` the round. */
export type CellPos = { row: number; col: number };

/** A block of cells, both ends included. */
export type CellRect = { top: number; left: number; bottom: number; right: number };

/** Why a cell keeps its value. */
export type CellRefusal =
  /** Only the Sets lens is written in the grid. */
  | "setsOnly"
  /** The exercise does not run in that round. */
  | "off"
  /** The round is done (decision 4). The caller knows which ones. */
  | "locked"
  /** What was typed is not a value of this lens. */
  | "parse"
  /** setSetCountInRound's own refusals. */
  | "lastSet"
  | "mixedBound";

/** The block between two corners, whichever order they come in. */
export const normaliseRect = (a: CellPos, b: CellPos): CellRect => ({
  top: Math.min(a.row, b.row),
  left: Math.min(a.col, b.col),
  bottom: Math.max(a.row, b.row),
  right: Math.max(a.col, b.col),
});

export const rectHas = (r: CellRect, p: CellPos): boolean =>
  p.row >= r.top && p.row <= r.bottom && p.col >= r.left && p.col <= r.right;

/** Why cell (slot, round `w`) cannot be typed into in `lens`, or null. */
export const cellRefusal = (slot: SlotDraft | undefined, w: number, lens: DraftGridLens): CellRefusal | null => {
  if (lens !== "sets") return "setsOnly";
  if (!slot || slot.inactiveWeeks[w] || setCountAt(slot, w) === 0) return "off";
  return null;
};

/** What the Sets field opens with, and what a copy carries: the set count in
 *  «Sets», in every other lens the text the cell shows (all the sets' reps,
 *  not the first's). Empty when the exercise is not in the round. */
export const cellValueText = (
  slot: SlotDraft | undefined,
  w: number,
  lens: DraftGridLens,
  o: DraftLineOptions,
  max?: MaxForProposal,
): string => {
  if (!slot) return "";
  return formatDraftGridCell(slot, w, lens, o, max) ?? "";
};

/** A set count as typed: a whole number from 1, else null. */
export const parseSetCount = (text: string): number | null => {
  const typed = text.trim();
  return /^\d+$/.test(typed) && Number(typed) >= 1 ? Number(typed) : null;
};

export type CellWrite = { ok: true; draft: Draft } | { ok: false; reason: CellRefusal };

/** Cell (`sIdx`, `at`) typed `text` in `lens`: only «Sets» writes, any other
 *  lens refuses with "setsOnly". */
export const writeCell = (draft: Draft, sIdx: number, at: CellPos, lens: DraftGridLens, text: string): CellWrite => {
  const slot = draft.sessions[sIdx]?.slots[at.row];
  const refused = cellRefusal(slot, at.col, lens);
  if (refused !== null) return { ok: false, reason: refused };
  const n = parseSetCount(text);
  if (n === null) return { ok: false, reason: "parse" };
  const res = setSetCountInRound(draft, sIdx, at.row, at.col, n);
  return res.ok ? { ok: true, draft: res.draft } : res;
};

/** A block of cells through `lens`, as `cellValueText` reads each. */
export const readRange = (
  draft: Draft,
  sIdx: number,
  rect: CellRect,
  lens: DraftGridLens,
  o: DraftLineOptions,
  maxOf?: (slot: SlotDraft) => MaxForProposal | undefined,
): string[][] => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const out: string[][] = [];
  for (let r = rect.top; r <= rect.bottom; r += 1) {
    const slot = slots[r];
    const row: string[] = [];
    for (let c = rect.left; c <= rect.right; c += 1) row.push(cellValueText(slot, c, lens, o, slot && maxOf?.(slot)));
    out.push(row);
  }
  return out;
};

export type RangeWrite = { draft: Draft; written: number; skipped: CellRefusal[] };

/** `matrix` written from cell `at` on, in one draft. A cell past the day or
 *  the cycle is left out, an empty value is passed over, and a cell that
 *  refuses keeps its value: `skipped` says why, one entry each. `locked`:
 *  the rounds done, which no cell of the block may change. */
export const writeRange = (
  draft: Draft,
  sIdx: number,
  at: CellPos,
  matrix: readonly (readonly string[])[],
  lens: DraftGridLens,
  locked: (week: number) => boolean = () => false,
): RangeWrite => {
  const rows = draft.sessions[sIdx]?.slots.length ?? 0;
  let next = draft;
  let written = 0;
  const skipped: CellRefusal[] = [];
  matrix.forEach((line, i) =>
    line.forEach((text, j) => {
      const pos = { row: at.row + i, col: at.col + j };
      if (pos.row >= rows || pos.col >= draft.cycleWeeks || text.trim() === "") return;
      if (locked(pos.col)) return void skipped.push("locked");
      const res = writeCell(next, sIdx, pos, lens, text);
      if (!res.ok) return void skipped.push(res.reason);
      if (res.draft !== next) written += 1;
      next = res.draft;
    }),
  );
  return { draft: next, written, skipped };
};

/** `src` repeated over `rows` × `cols`, as a spreadsheet's fill repeats it. */
const tileMatrix = (src: readonly (readonly string[])[], rows: number, cols: number): string[][] =>
  Array.from({ length: rows }, (_, i) => {
    const line = src[i % src.length] ?? [];
    return Array.from({ length: cols }, (_, j) => line[j % line.length] ?? "");
  });

const height = (r: CellRect) => r.bottom - r.top + 1;
const width = (r: CellRect) => r.right - r.left + 1;

/** What a paste writes on `rect`: the clipboard repeated when the block is a
 *  whole number of copies of it (one value on many cells), else as it is. */
export const pasteMatrix = (rect: CellRect, clip: readonly (readonly string[])[]): string[][] => {
  const rows = clip.length;
  const cols = Math.max(0, ...clip.map((l) => l.length));
  if (rows === 0 || cols === 0) return [];
  const tiles = height(rect) % rows === 0 && width(rect) % cols === 0;
  return tiles ? tileMatrix(clip, height(rect), width(rect)) : clip.map((l) => [...l]);
};

/** The block a fill writes: Ctrl+D repeats the top row down the block,
 *  Ctrl+R the left column across it. */
export const fillSource = (rect: CellRect, dir: "down" | "right"): CellRect =>
  dir === "down" ? { ...rect, bottom: rect.top } : { ...rect, right: rect.left };

/** Block `src` repeated over block `dest`, which holds it: a fill. The cells
 *  of `src` are not written again. */
export const fillRange = (
  draft: Draft,
  sIdx: number,
  src: CellRect,
  dest: CellRect,
  lens: DraftGridLens,
  o: DraftLineOptions,
  maxOf?: (slot: SlotDraft) => MaxForProposal | undefined,
  locked?: (week: number) => boolean,
): RangeWrite => {
  const tiles = tileMatrix(readRange(draft, sIdx, src, lens, o, maxOf), height(dest), width(dest));
  const matrix = tiles.map((l, i) => l.map((v, j) => (rectHas(src, { row: dest.top + i, col: dest.left + j }) ? "" : v)));
  return writeRange(draft, sIdx, { row: dest.top, col: dest.left }, matrix, lens, locked);
};

/** The fill handle dragged from block `rect` to cell `to`: the block grown
 *  down or right as far as `to`, the way it went further. Null when `to` is
 *  neither below nor right of it. */
export const dragFillRect = (rect: CellRect, to: CellPos): CellRect | null => {
  const down = to.row - rect.bottom;
  const right = to.col - rect.right;
  if (down <= 0 && right <= 0) return null;
  return down >= right ? { ...rect, bottom: to.row } : { ...rect, right: to.col };
};

/** Rows by line, cells by tab: what a spreadsheet copies and pastes. */
export const toTsv = (matrix: readonly (readonly string[])[]): string => matrix.map((l) => l.join("\t")).join("\n");

export const parseTsv = (text: string): string[][] => {
  const lines = text.replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n");
  return text === "" ? [] : lines.map((l) => l.split("\t"));
};
